import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Calendar,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Plus
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const leaveTypes = ["Annual Leave", "Sick Leave", "Family Responsibility", "Study Leave", "Maternity/Paternity", "Unpaid Leave"];

const AdminLeavePage = () => {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newLeave, setNewLeave] = useState({ leave_type: 'Annual Leave', start_date: '', end_date: '', reason: '' });
  const navigate = useNavigate();
  const token = localStorage.getItem("cdace_token");

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/admin/leave-requests`, { headers: { Authorization: `Bearer ${token}` } });
      setLeaveRequests(response.data.leave_requests);
    } catch (error) {
      if (error.response?.status === 401) navigate("/admin-login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [token, navigate]);

  const handleSubmit = async () => {
    try {
      await axios.post(`${API}/admin/leave-requests`, newLeave, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Leave request submitted");
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to submit leave request");
    }
  };

  const handleApproval = async (id, action) => {
    try {
      await axios.put(`${API}/admin/leave-requests/${id}/approve`, { action }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Leave request ${action}d`);
      fetchData();
    } catch (error) {
      toast.error(`Failed to ${action} leave request`);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#0b1c2d] flex items-center justify-center"><div className="text-[#c9a227]">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-[#0b1c2d] lg:ml-64 p-6" data-testid="admin-leave-page">
      <div className="mb-6">
        <Link to="/admin" className="flex items-center gap-2 text-slate-400 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" />Back to Dashboard</Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Leave Requests</h1>
            <p className="text-slate-400 mt-1">{leaveRequests.length} requests</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary"><Plus className="w-4 h-4 mr-2" />Request Leave</Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-700">
              <DialogHeader><DialogTitle className="text-white">Request Leave</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Leave Type</Label>
                  <select value={newLeave.leave_type} onChange={(e) => setNewLeave({...newLeave, leave_type: e.target.value})} className="w-full mt-1 p-2 bg-slate-800 border border-slate-700 rounded-sm text-white">
                    {leaveTypes.map(lt => <option key={lt} value={lt}>{lt}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Start Date</Label>
                    <Input type="date" value={newLeave.start_date} onChange={(e) => setNewLeave({...newLeave, start_date: e.target.value})} className="mt-1 bg-slate-800 border-slate-700 text-white" />
                  </div>
                  <div>
                    <Label className="text-slate-300">End Date</Label>
                    <Input type="date" value={newLeave.end_date} onChange={(e) => setNewLeave({...newLeave, end_date: e.target.value})} className="mt-1 bg-slate-800 border-slate-700 text-white" />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Reason</Label>
                  <Textarea value={newLeave.reason} onChange={(e) => setNewLeave({...newLeave, reason: e.target.value})} className="mt-1 bg-slate-800 border-slate-700 text-white" placeholder="Reason for leave..." />
                </div>
                <Button onClick={handleSubmit} className="w-full btn-primary">Submit Request</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="glass-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr><th>Employee</th><th>Type</th><th>From</th><th>To</th><th>Reason</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {leaveRequests.map((lr) => (
                  <tr key={lr.id}>
                    <td className="text-white">{lr.user_name}</td>
                    <td><Badge className="bg-blue-500/20 text-blue-400">{lr.leave_type}</Badge></td>
                    <td className="text-slate-400">{lr.start_date}</td>
                    <td className="text-slate-400">{lr.end_date}</td>
                    <td className="text-slate-400 max-w-[200px] truncate">{lr.reason}</td>
                    <td>
                      <Badge className={lr.status === 'approved' ? 'status-green' : lr.status === 'rejected' ? 'status-red' : 'status-yellow'}>{lr.status}</Badge>
                    </td>
                    <td>
                      {lr.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleApproval(lr.id, 'approve')} className="bg-green-600 hover:bg-green-700 h-7 px-2"><CheckCircle className="w-4 h-4" /></Button>
                          <Button size="sm" onClick={() => handleApproval(lr.id, 'reject')} className="bg-red-600 hover:bg-red-700 h-7 px-2"><XCircle className="w-4 h-4" /></Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLeavePage;
